import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';
import { muiTheme } from 'app/themes/materialUi';
import moment from 'moment';

import { BryntumTaskBoard } from '@bryntum/taskboard-react';
import Loader from 'app/components/atoms/Loader/Loader';
import { taskboardConfig } from './BetaTaskBoardConfig';
import { formatDate } from 'app/utils/date/date';

import { bind, memoize, debounce } from 'app/utils/decorators/decoratorUtils';
import { toUniqueUUID } from 'app/utils/string/string-utils';
import { get, set, sortBy } from 'app/utils/lo/lo';
import { isDefined, arrayObjectEquals } from 'app/utils/utils';
import { copyToClipboard, getSelectedPrimaryClass, normalizeEntityType } from 'app/utils/classification/classificationUtils';
import { getEntityUrl } from 'app/utils/entity/entityUtils';
import { updateEntity, loadEntity } from 'store/actions/entities/entitiesActions';
import { saveBoardPreferences } from 'store/actions/admin/usersActions';
import { setSelectedBoard } from 'store/actions/abox/boardActions';

import { showToastr } from 'store/actions/app/appActions';
import { saveComponentState } from 'store/actions/component/componentActions';
import { setColumnPosition, updateRelationColumnToBoard, loadBoardRecordsAll, loadBoardRecordsByColumn, BETA_BOARD_MAX_PER_COLUMN } from 'store/actions/abox/boardActions';
import { createRelationship, deleteRelationship } from 'store/actions/entities/relationshipsActions';

import '@bryntum/taskboard/taskboard.classic-dark.css';

const TooltipGlobalStyle = createGlobalStyle`
    .b-tooltip-content {
        color: ${muiTheme.colors.text.secondary} !important;
    }
`;

const BoardWrapper = styled.div`
    width: 100%;
    height: 100%;

    .b-taskboard-body {
        overflow: auto !important;
    }

    .b-taskboardbase {
        --taskboard-color: ${({ theme }) => theme.material.colors.text.primary} !important;
    }
    & .b-taskboardbase.b-taskdrag,
    & .b-panel .b-panel-body-wrap {
        background: transparent !important;
        --taskboard-taskdrag-drop-background: transparent !important;
        --taskboard-taskdrag-drop-border: 1px solid ${({ theme }) => theme.material.colors.background.divider} !important;
    }
    .b-taskboard {
        height: 100%;
        --taskboard-background: transparent;
        --taskboard-body-padding: 0em;
        --taskboard-column-gap: 2em;
        --taskboard-gap: 10px;
        --taskboard-column-background: transparent;
        --taskboard-column-border-radius: 3px;
        --taskboard-column-header-background: ${({ theme }) => theme.material.colors.background.default};
        --taskboard-column-header-icon-color: ${({ theme }) => theme.material.colors.text.secondary};
        --taskboard-swimlane-header-background: transparent;
        --taskboard-swimlane-header-font-size: 14px;
        --taskboard-column-header-font-size: 14px;
        --taskboard-column-header-color: ${({ theme }) => theme.material.colors.text.secondary};
        --taskboard-column-header-collapsed-title-top: 3rem;
        --taskboard-progressitem-color: ${({ theme }) => theme.material.colors.background.progress};
        --taskboard-column-min-width: 350px;
        --taskboard-card-footer-padding: 10px;
        --taskboard-card-footer-color: ${({ theme }) => theme.material.colors.text.caption} !important;
        --taskboard-swimlane-header-icon-color: ${({ theme }) => theme.material.colors.secondary.main};
    }

    & .b-taskboard-progress-outline {
        background: ${({ theme }) => theme.material.colors.background.progressLine} !important;
    }
    & .b-taskboard-body.b-horizontal-overflow .b-taskboard-column-headers {
        background: ${({ theme }) => theme.material.colors.background.default} !important;
    }
    & .b-taskboard-swimlane-header {
        background: ${({ theme }) => theme.material.colors.background.default};
    }
    & .b-taskboard-body.b-horizontal-overflow .b-taskboard-column-headers {
        background: ${({ theme }) => theme.material.colors.background.default};
    }

    & .b-taskboard-card-footer {
        color: #ffffff61 !important;
        font-size: 12px !important;
        min-height: 40px;
        padding: 0 10px;
    }
    & .footer-icon {
        font-size: 16px !important;
        position: relative;
        top: 2px;
    }
    & .b-taskboard-column-expander,
    .b-taskboard-swimlane-expander {
        transform: none;
    }
    & .b-taskboard-swimlane.b-collapsed .b-taskboard-swimlane-expander {
        transform: rotate(270deg);
    }
    & .b-taskboard-swimlane-title {
        letter-spacing: 1px;
        color: ${({ theme }) => theme.material.colors.text.primary};
    }
    & .b-taskboard-swimlane {
        border-bottom: 0.5px solid ${({ theme }) => theme.material.colors.background.divider} !important;
    }
    & .b-taskboard-swimlane-count {
        color: ${({ theme }) => theme.material.colors.text.secondary} !important;
    }
    & .b-taskboard-column-headers {
        width: auto !important;
        min-width: 100%;
        margin-bottom: 5px;
        border-bottom: 0.5px solid ${({ theme }) => theme.material.colors.background.divider} !important;
    }
    & .b-taskboard-swimlane-header {
        border-bottom: 0.5px solid ${({ theme }) => theme.material.colors.background.divider} !important;
        border-top: 0.5px solid ${({ theme }) => theme.material.colors.background.divider} !important;
    }
    & .b-taskboard-card {
        background: ${({ theme }) => theme.material.colors.background.paper};
        min-height: 80px !important;
        padding: 0.5rem !important;
        &:hover {
            background: ${({ theme }) => theme.material.colors.background.hover};
        }
    }
    & .b-taskboard-swimlane-header i {
        color: ${({ theme }) => theme.base.linkColor};
    }
    & .b-taskboard-resource-avatars {
        flex: 1;
    }
    & .b-taskboard-taskitem {
        order: 1;
    }
    & .b-taskboard-task-menu {
        transform: rotate(90deg) !important;
        cursor: pointer;
        height: 30px;
        width: 30px;
        cursor: pointer;
        &.b-widget .b-menuitem .b-box-item {
            color: #fff !important;
        }
        &:hover {
            background-color: ${({ theme }) => theme.material.colors.background.hover};
            border-radius: 50%;
        }
        &:before {
            font-size: 19px !important;
            color: ${({ theme }) => theme.material.colors.text.secondary} !important;
        }
    }

    & .title-icon {
        order: 0;
        color: ${({ theme }) => theme.material.colors.text.secondary};
        border-right: 1px solid ${({ theme }) => theme.material.colors.background.divider};
        padding-right: 10px;
        margin-right: 5px;
    }
    & .b-taskboard-card-header {
        font-size: 14px;
        letter-spacing: 0.25px;
        font-weight: 400;
    }
    .highest {
        border-left: 2px solid ${({ theme }) => get(theme.material.colors.priorityTaskColors, `1.1`)};
    }
    .high {
        border-left: 2px solid ${({ theme }) => get(theme.material.colors.priorityTaskColors, `2.1`)};
    }
    .medium {
        border-left: 2px solid ${({ theme }) => get(theme.material.colors.priorityTaskColors, `3.1`)};
    }
    .low {
        border-left: 2px solid ${({ theme }) => get(theme.material.colors.priorityTaskColors, `4.1`)};
    }
    .lowest {
        border-left: 2px solid ${({ theme }) => get(theme.material.colors.priorityTaskColors, `5.1`)};
    }
    .closed-task {
        border-left: 2px solid grey !important;
        background-color: rgba(255, 255, 255, 0.08);
        --taskboard-progressitem-color: grey !important;
    }
    .footer-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .due-date-title {
        font-size: 12px;
        color: ${({ theme }) => theme.material.colors.text.primary};
        }
    .due-date {
        font-size: 12px;
    }
    .due-past {
        color: ${({ theme }) => get(theme.material.colors.priorityTaskColors, `1.0`)};
    }
    .due-today,
    .due-tomorrow {
        color: ${({ theme }) => get(theme.material.colors.priorityTaskColors, `3.0`)};
    }
    .due-future {
        color: ${({ theme }) => theme.material.colors.text.secondary}
    }
`;

const reOrdering = columns =>
    (columns || []).map((column, index) => {
        const newColumn = { ...column };
        newColumn.order = index;
        return newColumn;
    });

class BetaBoardView extends PureComponent {
    static propTypes = {
        columns: PropTypes.arrayOf(PropTypes.object),
        list: PropTypes.array,
        taskItems: PropTypes.array,
        openSidebar: PropTypes.func.isRequired,
        groupBy: PropTypes.string,
        setColumnPosition: PropTypes.func,
        updateRelationColumnToBoard: PropTypes.func,
        userProfile: PropTypes.object,
    };

    static defaultProps = {
        columns: [],
        taskItems: [],
        swimlanes: [],
    };

    boardRef = React.createRef();
    boardRefLoaded = false;
    boardKey = 1;
    countersInitialized = false;
    initialColumnCounts = {};
    counterObservers = [];

    constructor(props) {
        super(props);
        this.state = {
            boardTasks: null,
            boardColumns: null,
            isLoading: false,
            boardKey: 0,
        };
        const { filterBy, excludeBy, orderBy, selectedBoard, reloadColumns } = this.props;

        if (selectedBoard) {
            reloadColumns(filterBy, excludeBy, orderBy);
        }
    }

    componentWillUnmount() {
        this.counterObservers.forEach(observer => observer.disconnect());
        this.counterObservers = [];
    }

    componentDidUpdate(prevProps) {
        const { reloadColumns, tasks, groupBy, selectedBoard, boardRecordsByColumn, boardRecordsAll, filterBy, excludeBy, orderBy, isLoading } = this.props;

        // Check if data just finished loading (isLoading went from true to false)
        const dataJustLoaded = prevProps.isLoading && !isLoading;

        // Check if board ID changed (not board object, because reset/save creates new object)
        const boardIdChanged = selectedBoard?.id && prevProps.selectedBoard?.id &&
            selectedBoard.id !== prevProps.selectedBoard.id;

        const isFilterChange = boardIdChanged ||
            filterBy !== prevProps.filterBy ||
            orderBy !== prevProps.orderBy ||
            excludeBy !== prevProps.excludeBy;

        // If board ID changed, clear old data immediately and increment key to force remount
        if (boardIdChanged) {
            this.boardKey++;
            // Destroy previous Bryntum instance to ensure clean remount
            const widget = this.boardRef?.current?.instance;
            if (widget) {
                try {
                    widget.destroy();
                } catch (e) {
                }
            }

            // Clear state to prevent ID collisions
            this.setState({
                boardTasks: null,
                boardColumns: null,
                isLoading: true,
                boardKey: this.boardKey,
            });

            return;
        }

        const isInitialLoad = !prevProps.boardRecordsAll?.records?.length && boardRecordsAll?.records?.length > 0;

        const isPagination = !isFilterChange && !isInitialLoad && (
            boardRecordsAll?.records !== prevProps.boardRecordsAll?.records ||
            boardRecordsByColumn?.records !== prevProps.boardRecordsByColumn?.records
        );

        // For pagination, append new tasks via Store API
        if (isPagination) {
            this.appendTasksViaStore(
                prevProps.boardRecordsAll?.records,
                boardRecordsAll?.records,
                prevProps.boardRecordsByColumn?.records,
                boardRecordsByColumn?.records
            );
        }
        // For initial load, filter change, or when data just finished loading, do full setState
        else if (isInitialLoad || isFilterChange || dataJustLoaded || prevProps.groupBy !== groupBy || tasks !== prevProps.tasks) {
            this.setState({ isLoading: true });

            if (isInitialLoad || isFilterChange || dataJustLoaded) {
                this.countersInitialized = false;
                this.initialColumnCounts = {
                    'All': boardRecordsAll?.count || 0
                };
                Object.keys(boardRecordsByColumn?.records || {}).forEach(columnId => {
                    this.initialColumnCounts[columnId] = boardRecordsByColumn.records[columnId]?.count || 0;
                });
            }

            this.setState(this.normalizeEntities(selectedBoard, boardRecordsByColumn.records, boardRecordsAll?.records, groupBy), () => {
                this.updateStateKey();
                if ((isInitialLoad || isFilterChange || dataJustLoaded) && !this.countersInitialized) {
                    setTimeout(() => {
                        this.updateColumnCounters();
                        this.countersInitialized = true;
                    }, 100);
                }
                // Force complete rerender of all cards
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const widget = this.boardRef?.current?.instance;
                        if (widget && widget.element) {
                            // Temporarily hide cards to force rerender
                            const cards = widget.element.querySelectorAll('.b-taskboard-card');
                            cards.forEach(card => {
                                card.style.display = 'none';
                            });
                            // Show cards again after a brief delay
                            setTimeout(() => {
                                cards.forEach(card => {
                                    card.style.display = '';
                                });
                                widget.refresh?.();
                            }, 50);
                        }
                    });
                });
            });
        }
        const columnsChanged = !arrayObjectEquals(
            selectedBoard?.primary?.['system_board/showcolumns'],
            prevProps.selectedBoard?.primary?.['system_board/showcolumns']
        );
        if (isFilterChange || columnsChanged) {
            reloadColumns(filterBy, excludeBy, orderBy);
        }

        if (boardRecordsAll?.records.length || boardRecordsByColumn?.records.length) {
            this.attachColumnScrollListeners();
        }
    }

    @bind
    normalizeTask(entity, columnId, groupBy) {
        const getSwimlane = ent => this.groupTaksToSwimlanes(ent, groupBy?.toLowerCase());

        return {
            ...entity,
            id: `${entity.id}-${columnId}`,
            taskId: entity.id,
            columnId,
            progress: entity?.primary?.progress ?? 0,
            prio: getSwimlane(entity)
        };
    }

    @bind
    appendTasksViaStore(prevAllRecords, newAllRecords, prevColumnRecords, newColumnRecords) {
        const reactWrapper = this.boardRef?.current;
        const widget = reactWrapper?.instance;
        if (!widget?.project?.taskStore) return;

        const taskStore = widget.project.taskStore;
        const { groupBy } = this.props;

        // Get new records that were added (backend returns no duplicates with proper filters)
        const prevAllLength = prevAllRecords?.length || 0;
        const newAllLength = newAllRecords?.length || 0;
        const newTasksFromAll = newAllLength > prevAllLength
            ? newAllRecords.slice(prevAllLength)
            : [];

        // For columns, find new tasks per column
        const newTasksFromColumns = [];
        Object.keys(newColumnRecords || {}).forEach(columnId => {
            const prevColRecords = prevColumnRecords?.[columnId]?.records || [];
            const newColRecords = newColumnRecords?.[columnId]?.records || [];

            // If there are more records now, take only the new ones (from the end)
            if (newColRecords.length > prevColRecords.length) {
                const newRecordsSlice = newColRecords.slice(prevColRecords.length);
                newRecordsSlice.forEach(record => {
                    newTasksFromColumns.push({ entity: record, columnId });
                });
            }
        });

        if (newTasksFromAll.length === 0 && newTasksFromColumns.length === 0) return;

        // Normalize and add new tasks (no duplicates from backend with proper filters)
        taskStore.beginBatch();
        try {
            // Add tasks from "All" column
            newTasksFromAll.forEach(entity => {
                const task = this.normalizeTask(entity, 'All', groupBy);
                taskStore.add(task);
            });

            // Add tasks from specific columns
            newTasksFromColumns.forEach(({ entity, columnId }) => {
                const task = this.normalizeTask(entity, columnId, groupBy);
                taskStore.add(task);
            });
        } finally {
            taskStore.endBatch();
            this.setState({ isLoading: false });
        }
    }

    @bind
    @debounce()
    attachColumnScrollListeners() {
        const { boardRecordsByColumn, boardRecordsAll } = this.props;
        const reactWrapper = this.boardRef?.current;
        const widget = reactWrapper?.instance;
        const rootEl = widget?.element || reactWrapper;

        if (!rootEl || !boardRecordsByColumn.records || !boardRecordsAll.records.length) return;
        const columnBodies = rootEl.querySelectorAll('.b-taskboard-column-body');
        if (!columnBodies.length) return;

        columnBodies.forEach(el => {
            if (!el._hasScrollListener) {
                el.addEventListener('scroll', this.handleColumnScroll, { passive: true });
                el._hasScrollListener = true;
            }
        });
    }

    @bind
    updateColumnCounters() {
        const reactWrapper = this.boardRef?.current;
        const widget = reactWrapper?.instance;

        if (!widget) return;

        this.counterObservers.forEach(observer => observer.disconnect());
        this.counterObservers = [];

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const rootEl = widget?.element || reactWrapper;

                widget.columns?.forEach(column => {
                    const columnId = column.id;
                    const count = this.initialColumnCounts[columnId] || 0;

                    const columnEl = rootEl.querySelector(`[data-column="${columnId}"]`);
                    const counterEl = columnEl?.querySelector('.b-taskboard-column-count');

                    if (counterEl) {
                        counterEl.textContent = `(${count})`;

                        const observer = new MutationObserver(() => {
                            const expectedText = `(${this.initialColumnCounts[columnId] || 0})`;
                            if (counterEl.textContent !== expectedText) {
                                counterEl.textContent = expectedText;
                            }
                        });

                        observer.observe(counterEl, {
                            childList: true,
                            characterData: true,
                            subtree: true
                        });

                        this.counterObservers.push(observer);
                    }
                });
            });
        });
    }

    @bind
    handleColumnScroll(e) {
        const el = e.target;

        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;

        const columnEl = el.closest('.b-taskboard-column');
        const columnId = columnEl?.dataset?.column;

        if (!nearBottom || this.state.isLoading) return;
        if (!columnEl) return;
        if (!columnId) return;

        if (columnId === 'All') {
            this.loadMore();
            return;
        }

        this.loadMoreColumn(columnId);
    }

    @bind
    canLoadMore() {
        const { boardRecordsAll } = this.props;
        const loaded = (boardRecordsAll.records || []).length;
        const total = boardRecordsAll.count || 0;

        if (total && loaded >= total) return false;
        if (loaded > 0 && loaded % BETA_BOARD_MAX_PER_COLUMN !== 0) return false;

        return true;
    }

    @bind
    canLoadMoreColumn(columnId) {
        const { boardRecordsByColumn } = this.props;
        const col = boardRecordsByColumn.records?.[columnId];

        if (!col) return false;

        const { records = [], count = 0, page = 1, pageSize = BETA_BOARD_MAX_PER_COLUMN } = col;
        const expectedLoaded = page * pageSize;

        // All records loaded - check against backend count
        if (count && records.length >= count) return false;

        // Check if last loaded page was incomplete (less than pageSize records)
        if (records.length < expectedLoaded) return false;

        return true;
    }

    @bind
    loadMore() {
        const { boardRecordsAll, loadBoardRecordsAll, selectedBoard, filterBy, excludeBy, orderBy, boardRecordsByColumn } = this.props;

        if (!selectedBoard) return;
        if (!this.canLoadMore()) return;

        const pageSize = BETA_BOARD_MAX_PER_COLUMN;
        const loaded = (boardRecordsAll.records || []).length;

        const nextPage = Math.floor(loaded / pageSize) + 1;

        const entityType = selectedBoard.primary['system_board/entitytype'].uri;
        const type = normalizeEntityType(entityType);

        // Build updated excludeBy with column filters
        const columnRecords = boardRecordsByColumn.records || {};
        const columnFilters = Object.values(columnRecords)
            .map(col => col.columnFilter)
            .filter(Boolean);

        const updatedExcludeBy = columnFilters.length > 0
            ? [{ or: columnFilters }, ...(excludeBy || [])]
            : excludeBy;

        this.setState({ isLoading: true }, () => {
            loadBoardRecordsAll({
                type,
                page: nextPage,
                pageSize,
                filterBy: filterBy || [],
                excludeBy: updatedExcludeBy,
                orderBy,
            }).finally(() => {
                this.setState({ isLoading: false });
            });
        });
    }

    @bind
    loadMoreColumn(columnId) {
        const { selectedBoard, boardRecordsByColumn, loadBoardRecordsByColumn, filterBy, excludeBy, orderBy } = this.props;

        if (!selectedBoard) return;
        if (!this.canLoadMoreColumn(columnId)) return;

        const col = boardRecordsByColumn.records[columnId];
        if (!col) return;

        const { page = 1, pageSize = BETA_BOARD_MAX_PER_COLUMN, columnFilter } = col;
        if (this.state.isLoading) return;

        const nextPage = page + 1;

        const entityType = selectedBoard.primary['system_board/entitytype'].uri;
        const type = normalizeEntityType(entityType);

        this.setState({ isLoading: true }, () => {
            loadBoardRecordsByColumn(columnId, {
                type,
                page: nextPage,
                pageSize,
                filterBy: columnFilter ? [...(filterBy || []), columnFilter] : filterBy,
                excludeBy,
                orderBy,
                columnFilter // Pass filter for next pagination
            }).finally(() => {
                this.setState({ isLoading: false });
            });
        });
    }



    @bind
    @memoize()
    buildColumns(selectedBoard) {
        const columns = selectedBoard.primary['system_board/showcolumns'];
        const boardColumns = [
            {
                text: 'All',
                id: 'All',
            },
            ...sortBy(
                columns
                    .map((clmn, index) => {
                        if (isDefined(clmn.isChecked) && !clmn.isChecked) {
                            return null;
                        }
                        return {
                            text: clmn.name ?? clmn.text ?? clmn.label,
                            id: clmn.id ?? clmn.value,
                            relationId: clmn.relationId,
                            order: clmn.order ?? index,
                        };
                    })
                    .filter(Boolean),
                ['order']
            ),
        ];
        return boardColumns;
    }

    @bind
    updateStateKey(newBoardKey) {
        const { viewState, saveComponentState } = this.props;
        const options = {
            ...viewState,
            boardKey: newBoardKey || viewState.boardKey + 1,
        };
        saveComponentState(this.props.id, options);
    }

    @bind
    buildSwimlanes() {
        const { tasks, groupBy } = this.props;
        if (!groupBy || !tasks) return;
        const swimlanes = [];
        switch (groupBy.toLowerCase()) {
            case 'assignee':
                var assignedTasks = tasks.filter(task => task.assignee);
                assignedTasks?.forEach(t => {
                    if (!swimlanes.some(x => x.id === t.assignee.id)) {
                        swimlanes.push({
                            id: t?.assignee?.id,
                            text: t?.assignee?.name.toUpperCase(),
                            collapsed: true,
                        });
                    }
                });
                if (tasks.some(x => !x.assignee)) {
                    swimlanes.push({
                        id: 0,
                        text: 'UNASSIGNED',
                        collapsed: true,
                    });
                }
                break;
            case 'priority':
                tasks.forEach(t => {
                    var priority = +t.primary.priority === 50 ? 3 : +t.primary.priority;
                    if (!swimlanes.some(x => x.id === priority)) {
                        swimlanes.push({
                            id: priority,
                            text: priority === 1 ? 'HIGHEST' : priority === 2 ? 'HIGH' : priority === 3 ? 'MEDIUM' : priority === 4 ? 'LOW' : 'LOWEST',
                            collapsed: true,
                        });
                    }
                });
                break;
            default:
                break;
        }
        return swimlanes.sort((a, b) => a.id - b.id);
    }

    @bind
    groupTaksToSwimlanes(task, groupBy) {
        switch (groupBy) {
            case 'assignee':
                return task.assignee?.id ?? 0;
            case 'priority':
                return task.primary.priority;
            default:
                break;
        }
    }

    @bind
    @memoize()
    normalizeEntities(selectedBoard, boardRecordsByColumn, boardRecordsAllRecords, groupBy) {
        const boardColumns = this.buildColumns(selectedBoard);

        const tasksData = [];
        const assignmentsData = [];

        const getSwimlane = entity => this.groupTaksToSwimlanes(entity, groupBy?.toLowerCase());

        boardColumns.forEach(column => {
            const columnId = column.id;
            let entities = [];
            if (columnId === 'All') {
                entities = boardRecordsAllRecords || [];
            } else {
                entities = boardRecordsByColumn?.[columnId]?.records || [];
            }

            entities.forEach(entity => {
                const task = {
                    ...entity,
                    id: `${entity.id}-${columnId}`,
                    taskId: entity.id,
                    columnId,
                    progress: entity?.primary?.progress ?? 0,
                    prio: getSwimlane(entity)
                };

                tasksData.push(task);

                assignmentsData.push({
                    id: assignmentsData.length,
                    event: task.id,
                    resource: entity.primary?.assignee ?? null
                });
            });
        });

        return {
            boardColumns,
            boardTasks: {
                tasksData,
                resourcesData: [],
                assignmentsData
            },
            isLoading: false
        };
    }

    @bind
    copyLinkToClipBoard(e, data) {
        e.stopPropagation();
        e.preventDefault();
        copyToClipboard(`${window.location.origin}/#${getEntityUrl(data.taskId, data.type)}`)
            .then(() => {
                this.props.showToastr({ severity: 'success', detail: 'Link copied to clipboard' });
            })
            .catch(() => {
                this.props.showToastr({ severity: 'error', detail: 'Link could not copied to clipboard' });
            });
    }
    @bind
    copyIdToClipBoard(e, id) {
        e.stopPropagation();
        e.preventDefault();
        copyToClipboard(id)
            .then(() => {
                this.props.showToastr({ severity: 'success', detail: 'ID copied to clipboard' });
            })
            .catch(() => {
                this.props.showToastr({ severity: 'error', detail: 'ID could not copied to clipboard' });
            });
    }

    @bind
    getDueStatus(date) {
        const now = moment().startOf('day');
        const due = moment(date).local().startOf('day');
        const diffDays = due.diff(now, 'days');

        switch (true) {
            case diffDays < 0:
                return 'past';
            case diffDays === 0:
                return 'today';
            case diffDays === 1:
                return 'tomorrow';
            default:
                return 'future';
        }
    }

    @bind
    buildFooterItems() {
        return {
            'id > resourceAvatars': {
                type: 'template',
                template: ({ taskRecord }) => {
                    const { data } = taskRecord;
                    const dueData = data.primary.dueDate ? formatDate(data.primary.dueDate) : '';
                    const dueStatus = dueData ? this.getDueStatus(data.primary.dueDate) : '';
                    const isTask = data?.type === 'system_task'
                    return `
                    <div class="footer-content">
                        <div class="id-link-wrapper">
                            <span id='UniqueUUID' title="Copy ID to clipboard">#${toUniqueUUID(taskRecord.taskId)}
                            </span> <i class="mdi mdi-link footer-icon" title="Copy link to clipboard"></i>
                        </div>
                        ${dueData && isTask ? `<span class="due-date-title">Due date: <span class="due-date due-${dueStatus}">${dueData}</span></span>` : ''}
                    </div>
                    `},
            },
        };
    }

    @bind
    getPrimaryShowColumns(selectedBoard) {
        const primaryShowColumns = get(selectedBoard, 'primary.system_board/showcolumns', []);
        const normalizePrimaryShowColumns = primaryShowColumns.map(col => ({
            ...col,
            type: normalizeEntityType(col?.type),
        }));
        return normalizePrimaryShowColumns;
    }

    @bind
    buildListeners() {
        return {
            //if task is closed , cancel operation
            beforeTaskDrag({ taskRecords }) {
                return !taskRecords[0]?.data?.primary?.closedDate;
            },
            beforeColumnDrag({ columnRecord }) {
                if (columnRecord.data.id === 'All') {
                    return false;
                }
            },

            columnDrag({ beforeColumn }) {
                if (beforeColumn?.data?.parentIndex === 0) {
                    return false;
                }
            },

            // taskDrop is triggered after a successful drop
            taskDrop: async ({ taskRecords, targetColumn, ...rest }) => {
                const {
                    updateEntity,
                    selectedBoard,
                    loadEntity,
                    primaryClasses,
                    createRelationship,
                    deleteRelationship,
                } = this.props;
                const { isLoading } = this.state;
                let { columnField } = this.props.parseColumnField(selectedBoard.primary['system_board/columnsetbyattribute']);
                const {
                    columnDefinitionId,
                    columnEntityType,
                    columnEntityPath,
                    columnEntityMultiple,
                    isReverseDefinition,
                    boardType,
                    columnEntity,
                } = this.props.parseColumnField(selectedBoard.primary['system_board/columnsetbyattribute']);
                columnField = `primary.${columnField}`;

                const entities = taskRecords.map(t => {
                    return {
                        data: t.data,
                        originaldata: t.originalData,
                        taskRecord: t,
                    };
                });

                // Compare target column with original column, not with current data.columnId
                if (targetColumn.data.id === entities[0].originaldata.columnId) return;
                // Remove the task relationship from source column on the backend
                if (!isLoading) await this.setState({ isLoading: true });

                const isDestinationAll = entities[0].data.columnId === 'All';
                let isDestinationAllValue = null;
                const uri = selectedBoard.primary['system_board/entitytype']?.uri;
                const primaryEntityClass = getSelectedPrimaryClass(uri, primaryClasses);
                const parseAllValue = list =>
                    list.forEach(field => {
                        if (field.children) {
                            parseAllValue(field.children);
                        }

                        if (field?.properties?.name === selectedBoard.primary['system_board/columnsetbyattribute'] && field?.properties?.name === 'priority') {
                            isDestinationAllValue = 3;
                        }
                    });
                parseAllValue(primaryEntityClass.formDefinition.fields);

                const updatedPromises = entities.map(async entity => {
                    let data = {
                        id: entity.data.taskId,
                        type: entity.data.type,
                    };
                    data = isDestinationAll ? set(data, columnField, isDestinationAllValue) : set(data, columnField, targetColumn.data.id);

                    const setData = (id, name, image, type) => {
                        return set(data, columnEntityPath, columnEntityMultiple ? [{ id, name, image, type }] : { id, name, image, type });
                    };

                    if (boardType === 'entityTypeahead' && targetColumn.data.id !== 'All') {
                        const column = columnEntity.find(col => col.id === targetColumn.data.id);
                        const { id, name, image, type } = await loadEntity(normalizeEntityType(column?.type), targetColumn.data.id);
                        data = setData(id, name, image, type);
                    }

                    if (
                        columnEntityType &&
                        !columnDefinitionId &&
                        boardType !== 'taskTypeahead' &&
                        boardType !== 'processTypeahead' &&
                        boardType !== 'entityTypeahead' &&
                        boardType !== 'userTypeahead'
                    ) {
                        if (isDestinationAll) {
                            data = set(data, columnEntityPath, isDestinationAllValue);
                        } else {
                            const { id, name, image, type } = await loadEntity(normalizeEntityType(columnEntityType), targetColumn.data.id);
                            data = setData(id, name, image, type);
                        }
                    }
                    const sourceColumnId = entity.originaldata?.columnId;
                    // Initialize relations if undefined
                    if (!entity.data.relations) {
                        entity.data.relations = [];
                    }
                    const relatedEntityRelation = entity.data.relations.find(rel => rel.relatedEntity.id === sourceColumnId);
                    const columnSetAtrr = selectedBoard.primary['system_board/columnsetbyattribute'];
                    const primaryShowColumns = this.getPrimaryShowColumns(selectedBoard);
                    let primaryTargetColumn = null;

                    switch (
                        boardType
                    ) {
                        case 'taskTypeahead':
                        case 'processTypeahead':
                        case 'workspaceTypeahead':
                        case 'teamTypeahead':
                            primaryTargetColumn = primaryShowColumns.find(col => col.id === targetColumn.data.id);
                            if (targetColumn.data.id === 'All') {
                                data = set(data, `primary.${columnSetAtrr}`, null);
                            } else {
                                const { id, name, type } = primaryTargetColumn;
                                data = set(data, `primary.${columnSetAtrr}`, columnEntityMultiple ? [{ id, name, type }] : { id, name, type });
                            }
                            return updateEntity(data);

                        case 'classificationTypeahead':
                            primaryTargetColumn = primaryShowColumns.find(col => col.id === targetColumn.data.id);
                            data = set(data, `primary.${columnSetAtrr}`, columnEntityMultiple ? [primaryTargetColumn] : primaryTargetColumn);
                            if (targetColumn.data.id === 'All') {
                                data = set(data, `primary.${columnSetAtrr}`, null);
                            }
                            return updateEntity(data);

                        case 'userTypeahead':
                            primaryTargetColumn = primaryShowColumns.find(col => col.id === targetColumn.data.id);
                            if (targetColumn.data.id === 'All') {
                                data = set(data, `primary.${columnSetAtrr}`, null);
                            } else {
                                const { id, name, username } = primaryTargetColumn
                                const value = columnEntityMultiple ? [{ id, name, username }] : { id, name, username }
                                data = set(data, `primary.${columnSetAtrr}`, value);
                            }
                            return updateEntity(data);

                        case 'typeahead':
                            primaryTargetColumn = primaryShowColumns.find(col => col.value === targetColumn.data.id);
                            if (columnEntityMultiple) {
                                data = set(data, `primary.${columnSetAtrr}`, [primaryTargetColumn?.value]);
                            }
                            if (targetColumn.data.id === 'All') {
                                data = set(data, `primary.${columnSetAtrr}`, null);
                            }
                            return updateEntity(data);

                        case 'entityTypeahead':
                            if (targetColumn.data.id === 'All') {
                                data = set(data, `primary.${columnSetAtrr}`, null);
                            } else {
                                const column = columnEntity.find(col => col.id === targetColumn.data.id);
                                const { id, name, image, type } = await loadEntity(normalizeEntityType(column?.type), targetColumn.data.id);
                                data = setData(id, name, image, type);
                            }
                            return updateEntity(data);
                    }

                    if (!columnDefinitionId) {
                        return updateEntity(data);
                    }

                    if (targetColumn.data.id === 'All') {
                        await deleteRelationship(relatedEntityRelation.relation.id);
                        // Remove relation from local data for next drag
                        entity.data.relations = entity.data.relations.filter(
                            rel => rel.relation.id !== relatedEntityRelation.relation.id
                        );
                        return;
                    }

                    if (!primaryShowColumns?.length) return;
                    const columnType = primaryShowColumns.find(col => col.id === data.primary[columnDefinitionId])?.type;
                    if (columnType) {
                        const rel = {
                            entity: { type: data.type, id: data.id },
                            relationDefinition: columnDefinitionId,
                            relatedEntity: { type: columnType, id: data.primary[columnDefinitionId] },
                        };
                        const relReverse = { ...rel, entity: rel.relatedEntity, relatedEntity: rel.entity };
                        if (sourceColumnId === 'All') {
                            const response = await createRelationship(isReverseDefinition ? relReverse : rel);
                            // Add new relation to local data for next drag
                            const newRelation = {
                                entity: rel.entity,
                                relatedEntity: rel.relatedEntity,
                                relation: { id: response.payload.relation.id },
                                isReverseRelation: isReverseDefinition
                            };
                            entity.data.relations.push(newRelation);
                            return;
                        }
                        if (relatedEntityRelation) {
                            await deleteRelationship(relatedEntityRelation.relation.id);
                            // Remove old relation from local data
                            entity.data.relations = entity.data.relations.filter(
                                rel => rel.relation.id !== relatedEntityRelation.relation.id
                            );
                        }
                        const response = await createRelationship(isReverseDefinition ? relReverse : rel);
                        // Add new relation to local data for next drag
                        const newRelation = {
                            entity: rel.entity,
                            relatedEntity: rel.relatedEntity,
                            relation: { id: response.payload.relation.id },
                            isReverseRelation: isReverseDefinition
                        };
                        entity.data.relations.push(newRelation);
                        return;
                    }
                });
                const updatedEntities = await Promise.all(updatedPromises);

                const sourceColumnId = entities[0].originaldata.columnId;
                const targetColumnId = entities[0].data.columnId;
                const movedCount = entities.length;

                if (this.initialColumnCounts[sourceColumnId] !== undefined) {
                    this.initialColumnCounts[sourceColumnId] = Math.max(0, this.initialColumnCounts[sourceColumnId] - movedCount);
                }
                if (this.initialColumnCounts[targetColumnId] !== undefined) {
                    this.initialColumnCounts[targetColumnId] = (this.initialColumnCounts[targetColumnId] || 0) + movedCount;
                }

                // Update originalData to reflect the new column so next drag works correctly
                entities.forEach(entity => {
                    if (entity.taskRecord && entity.taskRecord.originalData) {
                        entity.taskRecord.originalData.columnId = targetColumnId;
                    }
                });

                const reactWrapper = this.boardRef?.current;
                const widget = reactWrapper?.instance;

                if (widget?.project?.taskStore) {
                    // For Multiple Typeahead: handle duplicate tasks
                    if (columnEntityMultiple) {
                        entities.forEach(entity => {
                            if (targetColumnId === 'All') {
                                // Moving to All = clearing data = remove from ALL other columns
                                const allTasksForEntity = widget.project.taskStore.records.filter(
                                    task => task.taskId === entity.data.taskId && task.columnId !== 'All'
                                );
                                allTasksForEntity.forEach(task => {
                                    widget.project.taskStore.remove(task);
                                });
                            } else {
                                // Moving to specific column = check if duplicate exists in target
                                const targetTaskId = `${entity.data.taskId}-${targetColumnId}`;
                                const existingTask = widget.project.taskStore.getById(targetTaskId);
                                if (existingTask && existingTask !== entity.taskRecord) {
                                    widget.project.taskStore.remove(existingTask);
                                }
                            }
                        });
                    }

                    // Update task data with backend response (for both Single and Multiple)
                    updatedEntities.forEach((updatedEntity, index) => {
                        if (updatedEntity && entities[index]?.taskRecord) {
                            const taskRecord = entities[index].taskRecord;
                            taskRecord.set('primary', updatedEntity.primary || taskRecord.data.primary);
                        }
                    });
                }

                // Single refresh for all cases
                requestAnimationFrame(() => {
                    widget?.refresh?.();
                });

                this.setState({ isLoading: false }, () => {
                    this.updateColumnCounters();
                });
            }, columnDragEnd: async ({ beforeColumn, columnRecord }) => {
                const { isLoading, boardColumns: boardColumnsState } = this.state;
                if (beforeColumn === undefined) return;
                if (boardColumnsState.length < 2) return;

                const boardColumns = reOrdering(boardColumnsState);

                const oldIndex = boardColumns.find(col => col.id === columnRecord.originalData.id).order;

                let newIndex;
                if (oldIndex === boardColumns.length) {
                    newIndex = boardColumns.find(col => col.id === beforeColumn.originalData.id).order - 1;
                } else if (!beforeColumn) {
                    newIndex = boardColumns.length - 1;
                } else {
                    var index = boardColumns.find(col => col.id === beforeColumn.originalData.id).order;
                    newIndex = index >= 0 ? (oldIndex > index ? index : index - 1) : 0;
                }

                if (oldIndex === newIndex) return;
                if (!isLoading) await this.setState({ isLoading: true });

                const reorderedBoardColumns = reOrdering(this.reorderColumns(boardColumns, oldIndex, newIndex));
                this.saveColumnsOrder(reorderedBoardColumns.filter(col => col.id !== null));
            },
        };
    }

    reorderColumns(list, startIndex, endIndex) {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex < 0 ? 0 : endIndex, 0, removed);
        return result;
    }

    @bind
    async saveColumnsOrder(updatedColumns) {
        const { setColumnPosition, updateEntity, selectedBoard, preferences, saveBoardPreferences, setSelectedBoard } = this.props;
        setColumnPosition(updatedColumns);
        const columns = selectedBoard.primary['system_board/showcolumns'];
        const orderColumns = updatedColumns.filter(item => item.id !== 'All').map(item => item.id);
        const stabColumns = [...columns].sort((a, b) => orderColumns.indexOf(a.id) - orderColumns.indexOf(b.id));
        const board = await updateEntity({
            id: selectedBoard.id,
            type: selectedBoard.type,
            primary: {
                'system_board/showcolumns': stabColumns.map(column => ({
                    ...column,
                    order: updatedColumns.find(clmn => clmn.id === (column.id ?? column.value))?.order,
                })),
            },
        });
        await saveBoardPreferences({ ...preferences, betaBoards: { ...preferences.betaBoards, [board.id]: board } });
        await setSelectedBoard(board);
        this.setState({ isLoading: false });
    }

    taskRenderer({ taskRecord, cardConfig }) {
        const headerContent = cardConfig.children.header.children;
        headerContent.icon = {
            tag: 'i',
            class: 'mdi mdi-checkbox-marked-circle-outline',
            style: `color: ${muiTheme.colors.text.secondary} ;`,
        };
        var priority = +taskRecord?.primary?.priority === 50 ? 3 : +taskRecord?.primary?.priority;
        cardConfig.class[priority === 1 ? 'highest' : priority === 2 ? 'high' : priority === 3 ? 'medium' : priority === 4 ? 'low' : 'lowest'] = !!!taskRecord
            ?.primary?.closedDate;
        cardConfig.class['closed-task'] = !!taskRecord?.primary?.closedDate;
    }

    @bind
    onTaskClick(task) {
        const { taskRecord } = task;
        const { data } = taskRecord;
        if (task.event.target.className.includes('mdi-link footer-icon')) {
            this.copyLinkToClipBoard(task.event, data);
        }
        if (task.event.target.id.includes('UniqueUUID')) {
            this.copyIdToClipBoard(task.event, data.taskId);
        } else if (!task.event.target.className.includes('b-taskboard-task-menu') && !task.event.target.className.includes('mdi-link footer-icon')) {
            const { openSidebar, sidebarTitle } = this.props;
            const { filterBy, excludeBy, orderBy } = this.props;
            openSidebar && openSidebar(task.taskRecord.data, sidebarTitle, filterBy, excludeBy, orderBy);
        }
    }

    @bind
    updateKey(prevKey) {
        if (this.boardKey !== prevKey) {
            this.boardKey = this.boardKey + (prevKey || 0) + 1;
            this.updateStateKey(this.boardKey);
        }
    }

    render() {
        const { viewState, isLoading: isLoadingProp, groupBy } = this.props;
        const { boardKey } = viewState || {};
        const { isLoading, boardTasks, boardColumns } = this.state;
        this.updateKey(boardKey);
        return (
            <>
                <TooltipGlobalStyle />
                {(isLoading || isLoadingProp) && <Loader absolute backdrop />}
                {boardTasks && (
                    <BoardWrapper>
                        <BryntumTaskBoard
                            {...taskboardConfig()}
                            ref={this.boardRef}
                            key={`${this.props.selectedBoard?.id}-${this.boardKey}`}
                            columns={boardColumns}
                            swimlanes={this.buildSwimlanes(boardTasks || [], groupBy)}
                            project={boardTasks}
                            listeners={this.buildListeners()}
                            footerItems={this.buildFooterItems()}
                            taskRenderer={this.taskRenderer}
                            processItems={this.processItems}
                            onTaskClick={taskRecord => {
                                this.onTaskClick(taskRecord);
                            }}
                        />
                    </BoardWrapper>
                )}
            </>
        );
    }
}

export default connect(
    (state, props) => ({
        viewState: get(state, `component.state.${props.id}`),
        boardRecordsByColumn: state.abox.boards.boardRecordsByColumn,
        boardRecordsAll: state.abox.boards.boardRecordsAll,
        sidebarTitle: state.sidebar.title,
        userProfile: state.user.profile,
        preferences: state.user.preferences,
        primaryClasses: state.app.allPrimaryClasses.records,
    }),
    {
        saveComponentState,
        updateRelationColumnToBoard,
        setColumnPosition,
        showToastr,
        updateEntity,
        saveBoardPreferences,
        setSelectedBoard,
        loadEntity,
        createRelationship,
        deleteRelationship,
        loadBoardRecordsAll,
        loadBoardRecordsByColumn
    },
    null,
    { forwardRef: true }
)(BetaBoardView);
